import olMap from 'ol/Map';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import LineString from 'ol/geom/LineString';
import MultiLineString from 'ol/geom/MultiLineString';
import { Coordinate } from 'ol/coordinate';
import { createEditLineStyle, DEFAULT_ROUTE_COLOUR } from './GpxEditStyles';
import GPX from 'ol/format/GPX';
import * as olExtent from 'ol/extent';
import * as olProj from 'ol/proj';
import GeometryType from 'ol/geom/GeometryType';
import { Draw } from 'ol/interaction';
import { GpxRouteApi, IGpxWaypoint, IGpxWaypointUpsert, IGpxSaveResult } from './GpxRouteApi';
import { GpxRouteStore } from './GpxRouteStore';
import { GpxFeatureLayer } from './GpxFeatureLayer';
import { GpxWaypointLayer } from './GpxWaypointLayer';
import { GpxIconFactory } from './GpxIconFactory';
import { GpxLayerZIndex } from './GpxLayerOrder';
import { GpxWaypointDialog } from './GpxWaypointDialog';
import { GpxMetadataDialog } from './GpxMetadataDialog';
import { GpxEditToolbar, GpxEditSubMode } from './GpxEditToolbar';
import { GpxEditInteractions, IGpxEditInteractionsHost } from './GpxEditInteractions';
import {
    CommandStack,
    IWaypointDraft,
    IWaypointDraftHost,
    IRouteMetadata,
    AddWaypointCommand,
    DeleteWaypointCommand,
    UpdateWaypointCommand,
    MoveWaypointCommand,
    UpdateMetadataCommand,
    MultiGeometryCommand,
    GeometryCommand
} from './GpxEditCommands';
import { GpxEditEndpointLayer } from './GpxEditEndpointLayer';
import { MapModeManager } from '../map/MapModeManager';
import TrailwiseEventBus from '../TrailwiseEventBus';
import { normaliseColour } from './MapGpxUtils';

/** Sentinel route id for a brand-new (not yet saved) route's waypoints. */
const NEW_ROUTE_KEY = 'gpx-draft-new';

/**
 * Thinning tolerance for the editor, in real metres. Matches the server's
 * GpxGeoJson.SimplifyToleranceDegrees (0.00002° ≈ 2.2 m) — the tolerance the map's
 * /features endpoint has always drawn routes at — so thinning an older route leaves
 * exactly the line that was already on screen.
 */
const SIMPLIFY_TOLERANCE_METRES = 2.2;

/**
 * A map edit session for one GPX route: owns the working geometry layer, the
 * undo/redo stack, waypoint drafts and metadata state. Nothing is persisted
 * until Done — a single atomic save call — and Discard restores everything.
 */
export class GpxEditSession implements IWaypointDraftHost, IGpxEditInteractionsHost {

    private workingSource: VectorSource = new VectorSource();
    private workingLayer: VectorLayer;
    private endpointLayer: GpxEditEndpointLayer;
    private commandStack = new CommandStack();
    private interactions: GpxEditInteractions;
    private toolbar: GpxEditToolbar;

    private active = false;
    private completing = false;
    private editingGpxGuid: string | null = null;
    private isNewRoute = false;
    private metadata: IRouteMetadata = { displayName: '', notes: null, colourHex: DEFAULT_ROUTE_COLOUR, tags: [] };
    private drafts: Map<string, IWaypointDraft> = new Map();
    private originalWaypointGuids: string[] = [];
    private newDraftCounter = 0;
    private initialDraw: Draw | null = null;
    /** At least one point placed in the initial sketch — what Undo would remove. */
    private initialSketchHasPoints = false;
    private keydownListener: ((event: KeyboardEvent) => void) | null = null;

    public constructor(
        private map: olMap,
        private api: GpxRouteApi,
        private store: GpxRouteStore,
        private modeManager: MapModeManager,
        private featureLayer: GpxFeatureLayer,
        private waypointLayer: GpxWaypointLayer,
        private iconFactory: GpxIconFactory,
        private waypointDialog: GpxWaypointDialog,
        private metadataDialog: GpxMetadataDialog
    ) {
        this.workingLayer = new VectorLayer({
            source: this.workingSource,
            style: createEditLineStyle(this.metadata.colourHex),
            zIndex: GpxLayerZIndex.editLine
        });
        this.workingLayer.set('id', 'gpx-edit-working', true);
        this.workingLayer.setVisible(false);
        this.map.addLayer(this.workingLayer);

        this.endpointLayer = new GpxEditEndpointLayer(this.map);
        //one hook covers every way the geometry can change — commands, trim, Modify
        //drags mid-gesture — instead of a refresh call at each site. Skipped while a
        //sketch is running: Draw pulls the segment it is extending out of the source,
        //so the finish marker would jump backwards for the duration of the sketch.
        this.workingSource.on('change', () => {
            if (this.initialDraw || this.interactions?.activeSketchDraw) {
                return;
            }
            this.refreshEndpoints();
        });

        this.interactions = new GpxEditInteractions(
            this.map, this.workingSource, this.commandStack, this.waypointLayer, this,
            () => this.metadata.colourHex);

        this.toolbar = new GpxEditToolbar({
            setSubMode: (mode) => this.setSubMode(mode),
            openMetadata: () => { void this.openMetadataDialog(); },
            reverse: () => this.reverseRoute(),
            undo: () => this.undo(),
            redo: () => this.redo(),
            discard: () => { void this.discard(); },
            done: () => { void this.done(); }
        });

        this.commandStack.onChange = () => {
            this.refreshUndoRedoState();
            this.interactions.refreshForGeometryChange();
        };

        this.modeManager.registerLeaveGuard('editing', () => this.confirmLeave());

        document.getElementById('gpxEditErrorClose')?.addEventListener('click', () => {
            document.getElementById('gpxEditError')?.classList.add('d-none');
        });
    }

    get isDirty(): boolean {
        return this.commandStack.isDirty;
    }

    get isActive(): boolean {
        return this.active;
    }

    // -------------------------------------------------------------------------
    // Session lifecycle
    // -------------------------------------------------------------------------

    public startExisting = async (gpxGuid: string): Promise<void> => {
        if (this.active) {
            return;
        }
        if (!await this.modeManager.requestMode('editing')) {
            return;
        }

        try {
            const detail = await this.api.getRoute(gpxGuid);
            //route waypoints (server lazily backfills any legacy XML-embedded ones)
            const waypoints = await this.api.getRouteWaypoints(gpxGuid);

            this.editingGpxGuid = gpxGuid;
            this.isNewRoute = false;
            this.metadata = {
                displayName: detail.displayName,
                notes: detail.notes ?? null,
                colourHex: normaliseColour(detail.colourHex, DEFAULT_ROUTE_COLOUR),
                tags: detail.tags ?? []
            };

            //never edit the display-layer features — parse the full geometry from the XML
            this.loadWorkingGeometry(detail.gpxXml);
            this.loadWaypointDrafts(gpxGuid, waypoints);

            this.beginSession();
            this.fitViewIfNeeded();
        } catch (error) {
            console.error('Failed to start GPX edit session', error);
            this.showError('Unable to load the route for editing. Please try again.');
            this.completing = true;
            await this.modeManager.requestMode('idle');
            this.teardown();
        }
    }

    public startNew = async (): Promise<void> => {
        if (this.active) {
            return;
        }
        if (!await this.modeManager.requestMode('editing')) {
            return;
        }

        this.editingGpxGuid = null;
        this.isNewRoute = true;
        this.metadata = { displayName: '', notes: null, colourHex: DEFAULT_ROUTE_COLOUR, tags: [] };
        this.drafts.clear();
        this.originalWaypointGuids = [];

        this.beginSession();
        this.waypointLayer.setEditingRoute(NEW_ROUTE_KEY);
        this.startInitialDraw();
    }

    private beginSession(): void {
        this.active = true;
        this.completing = false;
        this.commandStack.clear();
        this.workingLayer.setVisible(true);
        this.applyEditLineColour();
        this.waypointLayer.setEditingRoute(this.editingGpxGuid ?? NEW_ROUTE_KEY);
        this.toolbar.show();
        this.toolbar.setUndoRedoState(false, false, false);
        this.installKeyboardShortcuts();
        this.refreshEndpoints();
        //a session opens with no tool selected: the user picks what to do, and
        //nothing on the map reacts to a stray click until they do
        this.setSubMode(null);
    }

    private teardown(): void {
        this.interactions.clearInteractions();
        this.removeInitialDraw();
        this.workingSource.clear();
        this.workingLayer.setVisible(false);
        this.endpointLayer.clear();
        this.drafts.clear();
        this.originalWaypointGuids = [];
        this.initialSketchHasPoints = false;
        this.commandStack.clear();
        this.toolbar.hide();
        this.toolbar.setActiveSubMode(null);
        this.waypointDialog.isOpen && this.waypointDialog.hide();
        this.metadataDialog.isOpen && this.metadataDialog.hide();
        this.waypointLayer.setEditingRoute(null);
        this.removeKeyboardShortcuts();
        this.active = false;
        this.editingGpxGuid = null;
    }

    /** Leave guard for the editing mode — veto unless clean, completing, or confirmed. */
    private confirmLeave = async (): Promise<boolean> => {
        if (!this.active || this.completing || !this.isDirty) {
            return true;
        }
        return this.showDiscardConfirm();
    }

    // -------------------------------------------------------------------------
    // Initial draw (new route)
    // -------------------------------------------------------------------------

    private startInitialDraw(): void {
        const self = this;

        this.initialDraw = new Draw({
            source: this.workingSource,
            type: GeometryType.LINE_STRING
        });
        this.map.addInteraction(this.initialDraw);
        const target = this.map.getTargetElement() as HTMLElement;
        if (target) {
            target.style.cursor = 'crosshair';
        }

        this.initialDraw.on('drawstart', function () {
            self.initialSketchHasPoints = true;
            self.refreshUndoRedoState();
        });

        this.initialDraw.on('drawend', function () {
            //defer so Draw finishes adding the feature to the source first
            window.setTimeout(() => {
                self.removeInitialDraw();
                self.initialSketchHasPoints = false;
                //record the line just drawn so Undo keeps peeling vertices off after
                //the sketch ends, exactly as it does after an extend
                self.pushInitialDrawCommands();
                //explicit: the source-change hook is suppressed while the sketch runs,
                //and finishing it doesn't itself raise another change
                self.refreshEndpoints();
                //same rule as opening a session: finish the sketch with no tool
                //selected, so a stray drag can't reshape the line just drawn
                self.setSubMode(null);
            }, 0);
        });
    }

    private removeInitialDraw(): void {
        if (this.initialDraw) {
            this.map.removeInteraction(this.initialDraw);
            this.initialDraw = null;
            const target = this.map.getTargetElement() as HTMLElement;
            if (target) {
                target.style.cursor = '';
            }
        }
    }

    // -------------------------------------------------------------------------
    // Working geometry
    // -------------------------------------------------------------------------

    private loadWorkingGeometry(gpxXml: string): void {
        this.workingSource.clear();

        const features = new GPX().readFeatures(gpxXml, {
            featureProjection: 'EPSG:3857'
        });

        //one working feature per track segment; wpt Point features are ignored
        //(waypoints come from the API as drafts)
        for (const feature of features) {
            const geometry = feature.getGeometry();
            if (geometry instanceof LineString) {
                this.addWorkingSegment(geometry);
            } else if (geometry instanceof MultiLineString) {
                for (const line of geometry.getLineStrings()) {
                    this.addWorkingSegment(line);
                }
            }
        }
    }

    /**
     * Adds one editable segment, made draggable in two steps.
     *
     * First, vertices that repeat the position of the one before them are dropped.
     * Coincident vertices break Modify outright: grabbing one of a pair moves it
     * alone, so the line runs out to the cursor and straight back to its twin.
     *
     * Then the segment is thinned (see thinForEditing). A recorded GPS track puts a
     * point every few metres, which on a typical import leaves a third of them within
     * one screen pixel of their neighbour — and a vertex you can't separate from the
     * next one can't be dragged either.
     *
     * Routes imported since the server started simplifying arrive thin already, and
     * both steps then find nothing to do; this is what makes the older ones editable.
     */
    private addWorkingSegment(line: LineString): void {
        const coordinates: Coordinate[] = [];
        for (const coordinate of line.getCoordinates()) {
            const previous = coordinates[coordinates.length - 1];
            //compare position only: a repeat carrying a different elevation is still
            //the same place on the map, and it is the map that has to be editable.
            //Thinning alone would not cover this: Douglas-Peucker always keeps the
            //first and last point, so a two-point coincident segment survives it.
            if (previous && previous[0] === coordinate[0] && previous[1] === coordinate[1]) {
                continue;
            }
            coordinates.push(coordinate);
        }

        if (coordinates.length < 2) {
            return;
        }
        this.workingSource.addFeature(new Feature({
            geometry: this.thinForEditing(new LineString(coordinates))
        }));
    }

    /**
     * Douglas-Peucker at the same tolerance the map already draws this route at, so
     * thinning changes nothing you can see — the editor simply stops carrying points
     * the display never showed.
     *
     * EPSG:3857 units are stretched by 1/cos(latitude), about 3.3 to the metre at
     * 52°N, so a tolerance quoted in metres must be divided by the local scale factor
     * or it would thin roughly twice as hard as intended this far north. The scale is
     * taken from the segment's own first point because the working source is still
     * being populated at this stage and has no extent to measure yet.
     */
    private thinForEditing(line: LineString): LineString {
        const metresPerUnit = olProj.getPointResolution('EPSG:3857', 1, line.getFirstCoordinate());
        const tolerance = metresPerUnit > 0
            ? SIMPLIFY_TOLERANCE_METRES / metresPerUnit
            : SIMPLIFY_TOLERANCE_METRES;

        const simplified = <LineString>line.simplify(tolerance);
        //OL returns the geometry untouched when it removes nothing
        return simplified.getCoordinates().length >= 2 ? simplified : line;
    }

    private fitViewIfNeeded(): void {
        const features = this.workingSource.getFeatures();
        if (features.length === 0) {
            return;
        }
        const extent = olExtent.createEmpty();
        for (const feature of features) {
            olExtent.extend(extent, feature.getGeometry().getExtent());
        }
        if (!olExtent.intersects(extent, this.map.getView().calculateExtent(this.map.getSize()))) {
            this.map.getView().fit(extent, { padding: [60, 60, 60, 60], maxZoom: 16 });
        }
    }

    // -------------------------------------------------------------------------
    // Sub-modes / toolbar actions
    // -------------------------------------------------------------------------

    /** Switches the active tool. null means no tool — nothing on the map responds. */
    private setSubMode(mode: GpxEditSubMode | null): void {
        if (!this.active || this.initialDraw) {
            return;
        }
        this.interactions.setSubMode(mode);
        this.toolbar.setActiveSubMode(mode);
    }

    /**
     * Flips the route's direction: the segments run back to front and each one's
     * points run in reverse. Both halves are needed — reversing only the points
     * would leave a multi-segment route jumping between its parts.
     *
     * The features themselves are left in place and only their coordinates are
     * swapped, because segment order here is whatever the vector source hands back
     * and re-adding features wouldn't reliably change it. That also makes this an
     * ordinary MultiGeometryCommand, so undo/redo needs nothing new.
     */
    private reverseRoute(): void {
        if (!this.active || this.initialDraw) {
            return;
        }
        //Reverse is an action, not a tool, so it leaves no tool selected. That also
        //settles the Extend case: Draw holds the segment it is sketching OUT of the
        //source until the sketch ends, and clearing the tool puts it back — without
        //this, reversing mid-sketch would silently drop that segment.
        this.setSubMode(null);

        const features = this.workingSource.getFeatures();
        //snapshot every segment BEFORE anything is mutated: each feature takes its
        //coordinates from a different feature, so reading them lazily would mix
        //reversed and unreversed state
        const before = features.map(feature => (<LineString>feature.getGeometry()).getCoordinates());
        if (before.every(coords => coords.length < 2)) {
            return;
        }

        const changes = features.map((feature, index) => ({
            feature,
            before: before[index],
            after: before[before.length - 1 - index].slice().reverse()
        }));

        this.commandStack.execute(new MultiGeometryCommand(
            'Reverse route',
            changes,
            () => this.interactions.refreshForGeometryChange()
        ));
    }

    /**
     * Turns the line just drawn into one undo entry per vertex, so Undo peels it
     * back a point at a time — the same thing the Extend tool does via
     * pushExtendCommands, rather than the drawn line being an un-undoable baseline.
     *
     * The first two points ARE the baseline: they are the least that is still a
     * line, so Undo stops there and leaves the working geometry saveable. Extend
     * has the original route as its floor; a fresh route has to invent one.
     *
     * alreadyApplied: the geometry already holds the full line, so these commands
     * only record how to walk back through it.
     */
    private pushInitialDrawCommands(): void {
        const feature = this.workingSource.getFeatures()[0];
        if (!feature) {
            return;
        }
        const coordinates = (<LineString>feature.getGeometry()).getCoordinates();

        for (let i = 2; i < coordinates.length; i++) {
            this.commandStack.execute(
                new GeometryCommand(
                    'Draw route',
                    feature,
                    coordinates.slice(0, i),
                    coordinates.slice(0, i + 1),
                    () => this.interactions.refreshForGeometryChange()),
                true);
        }
        //a two-point route pushes nothing, so nudge the toolbar into its dirty state
        this.refreshUndoRedoState();
    }

    /** Worth saving: a real edit, or a brand-new route that now has geometry. */
    private get hasUnsavedWork(): boolean {
        return this.isDirty || (this.isNewRoute && this.workingSource.getFeatures().length > 0);
    }

    /**
     * Drives the Undo/Redo buttons. While the initial route is being drawn, Undo
     * means "remove the point I just placed" (see undo()) rather than "pop the
     * command stack" — and the stack is still empty then, so deriving the button
     * state from it alone left Undo greyed out for the whole of drawing a new route.
     *
     * Redo has nothing to put back mid-sketch, so it stays disabled.
     *
     * The same applies while extending, except that Undo must stop once the newly
     * placed points are gone — Draw.extend() hands the sketch the whole existing
     * line, so carrying on would quietly shorten the route being extended.
     */
    private refreshUndoRedoState(): void {
        if (this.initialDraw) {
            this.toolbar.setUndoRedoState(this.initialSketchHasPoints, false, this.initialSketchHasPoints);
            return;
        }
        if (this.interactions.activeSketchDraw) {
            const placed = this.interactions.extendPointsPlaced > 0;
            this.toolbar.setUndoRedoState(placed, false, this.isDirty || placed);
            return;
        }
        this.toolbar.setUndoRedoState(this.commandStack.canUndo, this.commandStack.canRedo, this.hasUnsavedWork);
    }

    /** The extend sketch changed (GpxEditInteractions) — Undo may have become available. */
    public onSketchChanged = (): void => {
        this.refreshUndoRedoState();
    }

    /** Repositions the start/finish markers from the current working geometry. */
    private refreshEndpoints(): void {
        if (!this.active) {
            return;
        }
        this.endpointLayer.update(
            this.workingSource.getFeatures().map(f => (<LineString>f.getGeometry()).getCoordinates())
        );
    }

    private undo(): void {
        //while sketching, undo removes the last placed vertex
        if (this.initialDraw) {
            this.initialDraw.removeLastPoint();
            this.refreshUndoRedoState();
            return;
        }
        if (this.interactions.activeSketchDraw) {
            //stop at the point the extend started from: past that, removeLastPoint
            //would start deleting the route the user is extending
            if (this.interactions.extendPointsPlaced > 0) {
                this.interactions.activeSketchDraw.removeLastPoint();
                this.refreshUndoRedoState();
            }
            return;
        }
        this.commandStack.undo();
    }

    private redo(): void {
        this.commandStack.redo();
    }

    private async openMetadataDialog(): Promise<void> {
        if (!this.active) {
            return;
        }
        const result = await this.metadataDialog.show({ ...this.metadata, tags: [...this.metadata.tags] });
        if (result.action !== 'save') {
            return;
        }
        const before = { ...this.metadata, tags: [...this.metadata.tags] };
        this.commandStack.execute(new UpdateMetadataCommand(
            (metadata) => {
                this.metadata = { ...metadata, tags: [...metadata.tags] };
                this.applyEditLineColour();
            },
            before,
            result.values));
    }

    /** Re-applies the working line style in the current route's colour. */
    private applyEditLineColour(): void {
        this.workingLayer.setStyle(createEditLineStyle(this.metadata.colourHex));
    }

    // -------------------------------------------------------------------------
    // Done / discard
    // -------------------------------------------------------------------------

    private async done(): Promise<void> {
        if (!this.active) {
            return;
        }

        if (this.workingSource.getFeatures().length === 0) {
            this.showError('Draw the route line before saving.');
            return;
        }

        //existing route with no changes: nothing to save
        if (!this.isNewRoute && !this.isDirty) {
            this.completing = true;
            if (await this.modeManager.requestMode('idle')) {
                this.teardown();
            }
            return;
        }

        //a new route needs its details before the first save
        if (this.isNewRoute) {
            const result = await this.metadataDialog.show(
                { ...this.metadata, tags: [...this.metadata.tags] },
                'Save new route');
            if (result.action !== 'save') {
                return;
            }
            this.metadata = result.values;
        }

        const saved = await this.save();
        if (!saved) {
            return;
        }

        this.completing = true;
        if (await this.modeManager.requestMode('idle')) {
            this.teardown();
        }
    }

    private async save(): Promise<boolean> {
        const segments = this.collectSegments();
        const upserts = this.collectWaypointUpserts();

        try {
            let result: IGpxSaveResult;
            if (this.isNewRoute) {
                result = await this.api.createDrawnRoute({
                    displayName: this.metadata.displayName,
                    notes: this.metadata.notes,
                    colourHex: this.metadata.colourHex,
                    segments: segments,
                    waypoints: upserts,
                    tags: this.metadata.tags
                });
            } else {
                const deletes = this.originalWaypointGuids
                    .filter(guid => !this.drafts.has(guid));
                result = await this.api.saveEditSession(this.editingGpxGuid, {
                    displayName: this.metadata.displayName,
                    notes: this.metadata.notes ?? '',
                    colourHex: this.metadata.colourHex,
                    segments: segments,
                    waypointUpserts: upserts,
                    waypointDeletes: deletes,
                    tags: this.metadata.tags
                });
            }

            //refresh map + panel from the saved state
            this.featureLayer.refresh();
            TrailwiseEventBus.gpxRoutesChanged(result.route.gpxGuid);
            return true;
        } catch (error) {
            console.error('Failed to save GPX edit session', error);
            this.showError('Unable to save the route. Please check your connection and try again.');
            return false;
        }
    }

    private async discard(): Promise<void> {
        if (!this.active) {
            return;
        }
        if (this.hasUnsavedWork) {
            if (!await this.showDiscardConfirm()) {
                return;
            }
        }
        this.completing = true;
        if (await this.modeManager.requestMode('idle')) {
            this.teardown();
        }
    }

    private collectSegments(): number[][][] {
        return this.workingSource.getFeatures().map(feature => {
            const geometry = (<LineString>feature.getGeometry()).clone();
            geometry.transform('EPSG:3857', 'EPSG:4326');
            return geometry.getCoordinates().map(c => [c[0], c[1]]);
        });
    }

    private collectWaypointUpserts(): IGpxWaypointUpsert[] {
        const upserts: IGpxWaypointUpsert[] = [];
        let sortOrder = 0;
        this.drafts.forEach(draft => {
            upserts.push({
                waypointGuid: draft.waypointGuid,
                clientRef: draft.clientRef,
                lat: draft.lonLat[1],
                lon: draft.lonLat[0],
                name: draft.name,
                description: draft.description,
                waypointTypeId: draft.typeId,
                sortOrder: sortOrder++
            });
        });
        return upserts;
    }

    // -------------------------------------------------------------------------
    // IGpxEditInteractionsHost
    // -------------------------------------------------------------------------

    public onGeometryChanged = (): void => {
        //trim dots etc. are refreshed by the interactions module via
        //refreshForGeometryChange (driven from commandStack.onChange)
    }

    public onSubModeChanged = (mode: GpxEditSubMode): void => {
        //authoritative switch: updates the interactions AND the toolbar highlight
        this.setSubMode(mode);
    }

    public onWaypointAddRequested = (lonLat: [number, number]): void => {
        void (async () => {
            const result = await this.waypointDialog.show(null, false);
            if (result.action !== 'save') {
                return;
            }
            const clientRef = `new-${++this.newDraftCounter}`;
            const draft: IWaypointDraft = {
                key: clientRef,
                waypointGuid: null,
                clientRef: clientRef,
                name: result.values.name,
                description: result.values.description,
                typeId: result.values.typeId,
                lonLat: lonLat
            };
            this.commandStack.execute(new AddWaypointCommand(this, draft));
        })();
    }

    public onWaypointEditRequested = (feature: Feature): void => {
        const key = <string>feature.get('waypointGuid');
        const draft = this.drafts.get(key);
        if (!draft) {
            return;
        }
        void (async () => {
            const result = await this.waypointDialog.show(
                { name: draft.name, description: draft.description, typeId: draft.typeId },
                true);
            if (result.action === 'delete') {
                this.commandStack.execute(new DeleteWaypointCommand(this, { ...draft, lonLat: [...draft.lonLat] as [number, number] }));
            } else if (result.action === 'save') {
                const before = { name: draft.name, description: draft.description, typeId: draft.typeId };
                if (before.name !== result.values.name ||
                    before.description !== result.values.description ||
                    before.typeId !== result.values.typeId) {
                    this.commandStack.execute(new UpdateWaypointCommand(this, key, before, result.values));
                }
            }
        })();
    }

    public onWaypointMoved = (feature: Feature, beforeLonLat: [number, number], afterLonLat: [number, number]): void => {
        const key = <string>feature.get('waypointGuid');
        if (!this.drafts.has(key)) {
            return;
        }
        //the Translate drag already moved the feature — record the inverse
        const draft = this.drafts.get(key);
        draft.lonLat = afterLonLat;
        this.commandStack.execute(new MoveWaypointCommand(this, key, beforeLonLat, afterLonLat), true);
    }

    // -------------------------------------------------------------------------
    // IWaypointDraftHost (commands mutate session waypoint state through this)
    // -------------------------------------------------------------------------

    public addDraft = (draft: IWaypointDraft): void => {
        this.drafts.set(draft.key, draft);
        //NOTE: the feature's `waypointGuid` slot carries the draft KEY (a clientRef
        //for new drafts, the real guid for existing ones) — it's the lookup key the
        //interaction handlers read back via feature.get('waypointGuid').
        const feature = this.waypointLayer.toFeature({
            waypointGuid: draft.key,
            gpxGuid: this.editingGpxGuid ?? NEW_ROUTE_KEY,
            waypointTypeId: draft.typeId,
            name: draft.name,
            description: draft.description,
            lat: draft.lonLat[1],
            lon: draft.lonLat[0],
            sortOrder: 0
        });
        this.waypointLayer.addFeature(feature);
    }

    public removeDraft = (key: string): void => {
        this.drafts.delete(key);
        const feature = this.waypointLayer.getFeatureByGuid(key);
        if (feature) {
            this.waypointLayer.removeFeature(feature);
        }
    }

    public applyDraftFields = (key: string, fields: { name: string; description: string | null; typeId: number }): void => {
        const draft = this.drafts.get(key);
        if (!draft) {
            return;
        }
        draft.name = fields.name;
        draft.description = fields.description;
        draft.typeId = fields.typeId;

        const feature = this.waypointLayer.getFeatureByGuid(key);
        if (feature) {
            feature.set('name', fields.name);
            feature.set('description', fields.description);
            feature.set('waypointTypeId', fields.typeId);
            feature.changed();
        }
    }

    public setDraftPosition = (key: string, lonLat: [number, number]): void => {
        const draft = this.drafts.get(key);
        if (!draft) {
            return;
        }
        draft.lonLat = lonLat;

        const feature = this.waypointLayer.getFeatureByGuid(key);
        if (feature) {
            (<Point>feature.getGeometry()).setCoordinates(olProj.fromLonLat(lonLat));
        }
    }

    private loadWaypointDrafts(gpxGuid: string, waypoints: IGpxWaypoint[]): void {
        this.drafts.clear();
        this.originalWaypointGuids = waypoints.map(w => w.waypointGuid);
        for (const waypoint of waypoints) {
            this.drafts.set(waypoint.waypointGuid, {
                key: waypoint.waypointGuid,
                waypointGuid: waypoint.waypointGuid,
                clientRef: null,
                name: waypoint.name,
                description: waypoint.description ?? null,
                typeId: waypoint.waypointTypeId,
                lonLat: [waypoint.lon, waypoint.lat]
            });
        }
        this.waypointLayer.setRouteWaypoints(gpxGuid, waypoints);
    }

    // -------------------------------------------------------------------------
    // Dialog helpers / keyboard
    // -------------------------------------------------------------------------

    private showDiscardConfirm(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            const dialog = document.getElementById('gpxDiscardConfirm');
            const keepButton = document.getElementById('gpxDiscardKeep');
            const discardButton = document.getElementById('gpxDiscardConfirmBtn');

            const close = (confirmed: boolean) => {
                dialog.classList.add('d-none');
                keepButton.removeEventListener('click', onKeep);
                discardButton.removeEventListener('click', onDiscard);
                resolve(confirmed);
            };
            const onKeep = () => close(false);
            const onDiscard = () => close(true);

            keepButton.addEventListener('click', onKeep);
            discardButton.addEventListener('click', onDiscard);
            dialog.classList.remove('d-none');
        });
    }

    private showError(message: string): void {
        const dialog = document.getElementById('gpxEditError');
        const messageElement = document.getElementById('gpxEditErrorMessage');
        if (dialog && messageElement) {
            messageElement.textContent = message;
            dialog.classList.remove('d-none');
        }
    }

    private installKeyboardShortcuts(): void {
        const self = this;
        this.keydownListener = (event: KeyboardEvent) => {
            const target = <HTMLElement>event.target;
            if (target && ['INPUT', 'TEXTAREA', 'SELECT'].indexOf(target.tagName) >= 0) {
                return;
            }
            if (event.ctrlKey && !event.shiftKey && event.key.toLowerCase() === 'z') {
                event.preventDefault();
                self.undo();
            } else if ((event.ctrlKey && event.key.toLowerCase() === 'y') ||
                (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === 'z')) {
                event.preventDefault();
                self.redo();
            }
        };
        document.addEventListener('keydown', this.keydownListener);
    }

    private removeKeyboardShortcuts(): void {
        if (this.keydownListener) {
            document.removeEventListener('keydown', this.keydownListener);
            this.keydownListener = null;
        }
    }

}
